import { deckEmptyState, type DeckEmptyStateInfo } from './deckEmptyState.ts';
import type { DeckFilterState } from './deckFilterState.ts';
import type { DeckModelOption } from './useDeckFilters.ts';

/**
 * The one line the page shows in place of a body it has nothing to fill.
 *
 * Quiet by design: a sentence and a single button, in the weight of the rest of
 * the page rather than a centred illustration. The sentence is chosen in
 * `deckEmptyState.ts` — everything here is the rendering.
 *
 * It is deliberately NOT a live region. `DeckSearchBar` already announces
 * "Showing 0 of 62 decks" politely once the typing settles, and two regions
 * firing on the same keystroke is how a screen reader ends up reading a search
 * twice. The heading level is left alone for the same reason: this replaces a
 * list, not a section.
 */
type Props = {
  filters: DeckFilterState;
  models: readonly DeckModelOption[];
  trashRetentionDays?: number;
  /** An owner id back to a name, for the Owner sentence. */
  personName?: (id: string) => string | null;
  /** The page's clear-all, the same one the chip row's button calls. */
  onClearFilters: () => void;
  onCreateDeck: () => void;
};

export default function DeckEmptyState({
  filters,
  models,
  trashRetentionDays,
  personName,
  onClearFilters,
  onCreateDeck,
}: Props) {
  const info: DeckEmptyStateInfo = deckEmptyState({ filters, models, trashRetentionDays, personName });
  return (
    <div className="deck-empty" data-empty={info.key}>
      <p className="deck-empty__line">{info.message}</p>
      {info.action && (
        <button
          type="button"
          className={info.action.kind === 'new' ? 'btn btn--primary btn--sm' : 'btn btn--ghost btn--sm'}
          onClick={info.action.kind === 'new' ? onCreateDeck : onClearFilters}
        >
          {info.action.label}
        </button>
      )}
    </div>
  );
}
